import { z } from "zod";

/**
 * Notifications (docs/15-zusammenarbeit.md): an in-app feed per user plus
 * bundled e-mails for the categories the user wants.
 */

export const NOTIFICATION_TYPES = [
  /** Someone commented on a file I created (or commented on before). */
  "comment",
  /** A reply in a thread I started or took part in. */
  "reply",
  /** Someone mentioned me (@) in a comment. */
  "mention",
  /** A thread I took part in was marked as done (in-app only). */
  "resolved",
  /** My print PDF / video export is ready or failed. */
  "export_done",
  "export_failed",
  /** Organization membership (in-app only; adding already sends its own mail). */
  "member_added",
  "role_changed",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** Categories a user can switch on or off for e-mails (in-app is always on). */
export const NOTIFICATION_EMAIL_CATEGORIES = [
  "comments",
  "replies",
  "mentions",
  "exports",
] as const;
export type NotificationEmailCategory = (typeof NOTIFICATION_EMAIL_CATEGORIES)[number];

export const NOTIFICATION_EMAIL_LABELS: Record<
  NotificationEmailCategory,
  { label: string; description: string }
> = {
  comments: {
    label: "Neue Kommentare",
    description:
      "Zu Dateien, die du erstellt oder schon kommentiert hast – auch von Gästen eines geteilten Links.",
  },
  replies: {
    label: "Antworten",
    description: "In Unterhaltungen, die du begonnen oder in denen du geschrieben hast.",
  },
  mentions: {
    label: "Erwähnungen",
    description: "Wenn dich jemand mit @ in einem Kommentar erwähnt (kommt am schnellsten).",
  },
  exports: {
    label: "Exporte",
    description: "Wenn dein Video oder Druck-PDF fertig ist oder nicht erstellt werden konnte.",
  },
};

/** Which e-mail category a type belongs to (null = in-app only). */
export const NOTIFICATION_EMAIL_CATEGORY: Record<
  NotificationType,
  NotificationEmailCategory | null
> = {
  comment: "comments",
  reply: "replies",
  mention: "mentions",
  resolved: null,
  export_done: "exports",
  export_failed: "exports",
  member_added: null,
  role_changed: null,
};

/** Days a notification stays before the maintenance job deletes it. */
export const NOTIFICATION_RETENTION_DAYS = 90;

export const notificationSchema = z.object({
  id: z.uuid(),
  type: z.enum(NOTIFICATION_TYPES),
  organization: z.object({ id: z.string(), name: z.string() }),
  /** Who did it; null name = no person (e.g. an export finished). */
  actor: z.object({ id: z.string().nullable(), name: z.string().nullable(), guest: z.boolean() }),
  document: z
    .object({ id: z.uuid(), title: z.string(), kind: z.enum(["design", "video"]) })
    .nullable(),
  commentId: z.uuid().nullable(),
  /** First comment of the thread (deep link `?comment=`). */
  threadId: z.uuid().nullable(),
  /** Plain text: comment excerpt, export error … */
  snippet: z.string().nullable(),
  /** Type-specific extras: export format and file name, new role … */
  details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  createdAt: z.date(),
  readAt: z.date().nullable(),
});
export type AppNotification = z.infer<typeof notificationSchema>;

export const notificationListSchema = z.object({
  items: z.array(notificationSchema),
  unread: z.number(),
  /** Pass as `before` for older entries; null = nothing older. */
  nextCursor: z.date().nullable(),
});
export type NotificationList = z.infer<typeof notificationListSchema>;

export const notificationPreferencesSchema = z.object({
  email: z.object(
    Object.fromEntries(
      NOTIFICATION_EMAIL_CATEGORIES.map((category) => [category, z.boolean()]),
    ) as {
      [K in NotificationEmailCategory]: z.ZodBoolean;
    },
  ),
  /** Whether the server can send mails at all (SMTP configured). */
  emailAvailable: z.boolean(),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/** Members that can be mentioned in a comment. */
export const mentionableSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  image: z.string().nullable(),
});
export type Mentionable = z.infer<typeof mentionableSchema>;

const EXPORT_LABELS: Record<string, string> = {
  mp4: "Video-Export",
  "pdf-print": "Druck-PDF",
};

/**
 * One notification as a sentence, split into the actor (shown bold in the app)
 * and the rest: "Emil Editor" + "hat dich in „Flyer“ erwähnt". Used by the feed
 * and the mails.
 */
export function describeNotification(n: {
  type: NotificationType;
  actorName: string | null;
  documentTitle: string | null;
  organizationName: string;
  details: Record<string, unknown>;
}): { actor: string | null; text: string } {
  const title = `„${n.documentTitle ?? "Datei"}“`;
  const actor = n.actorName;
  const exportLabel = EXPORT_LABELS[String(n.details.format ?? "")] ?? "Export";
  const role = typeof n.details.roleLabel === "string" ? n.details.roleLabel : null;
  switch (n.type) {
    case "comment":
      return { actor, text: `hat ${title} kommentiert` };
    case "reply":
      return { actor, text: `hat in ${title} geantwortet` };
    case "mention":
      return { actor, text: `hat dich in ${title} erwähnt` };
    case "resolved":
      return { actor, text: `hat eine Unterhaltung in ${title} als erledigt markiert` };
    case "export_done":
      return { actor: null, text: `Dein ${exportLabel} von ${title} ist fertig` };
    case "export_failed":
      return { actor: null, text: `Dein ${exportLabel} von ${title} ist fehlgeschlagen` };
    case "member_added":
      return actor
        ? {
            actor,
            text: `hat dich zu „${n.organizationName}“ hinzugefügt${role ? ` (${role})` : ""}`,
          }
        : { actor: null, text: `Du bist jetzt Mitglied von „${n.organizationName}“` };
    case "role_changed":
      return actor
        ? {
            actor,
            text: `hat deine Rolle in „${n.organizationName}“ geändert${role ? `: ${role}` : ""}`,
          }
        : {
            actor: null,
            text: `Deine Rolle in „${n.organizationName}“ ist jetzt ${role ?? "eine andere"}`,
          };
  }
}
